/*
 * Copyright(c) 1995 - 2020 T-Systems Multimedia Solutions GmbH
 * Riesaer Str. 5, 01129 Dresden
 * All rights reserved.
 */

import { Directive, HostListener, OnDestroy, Output } from '@angular/core';
import { Observable, Subject, fromEvent, merge } from 'rxjs';
import { finalize, switchMap, take, takeUntil } from 'rxjs/operators';
import { WindowRef } from '../utils/window-ref';


@Directive({
    selector: '[commonDrag]'
})
export class DragDirective implements OnDestroy {

    @Output('commonDrag') drag$: Observable<MouseEvent | TouchEvent>;

    @Output('commonDragEnd') dragEnd$ = new Subject<void>();

    @Output('commonDragStart')
    dragSequence$ = new Subject<Observable<MouseEvent | TouchEvent>>();

    private windowMouseMove$ = merge(
        fromEvent<MouseEvent>(this.windowRef.nativeWindow, 'mousemove'),
        fromEvent<TouchEvent>(this.windowRef.nativeWindow, 'touchmove')
    );

    private dragStop$ = merge(
        fromEvent<TouchEvent>(this.windowRef.nativeWindow, 'touchend'),
        fromEvent<TouchEvent>(this.windowRef.nativeWindow, 'touchcancel'),
        fromEvent<MouseEvent>(this.windowRef.nativeWindow, 'mouseup'),
        fromEvent<MouseEvent>(this.windowRef.nativeWindow, 'mousedown'),
        fromEvent<DragEvent>(this.windowRef.nativeWindow, 'dragend')
    )

    constructor(
        private windowRef: WindowRef
    ) {
        this.drag$ = this.dragSequence$.pipe(
            // Flatten events into one mouse move event.
            switchMap(sequence$ => sequence$.pipe(
                finalize(() => this.dragEnd$.next())
            ))
        );
    }

    ngOnDestroy(): void {
        this.dragSequence$.complete();
    }

    @HostListener('mousedown', ['$event'])
    @HostListener('touchstart', ['$event'])
    startDrag(e: MouseEvent | TouchEvent): void {
        e.stopPropagation();

        const dragStop$ = this.dragStop$.pipe(
            take(1)
        );
        const mouseMove$ = this.windowMouseMove$.pipe(
            takeUntil(dragStop$),
            take(1)
        );
        mouseMove$.subscribe(() => {
            const drag$ = this.windowMouseMove$.pipe(
                takeUntil(dragStop$)
            );
            this.dragSequence$.next(drag$);
        });
    }
}
